import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, ArrowRight } from "lucide-react";

/**
 * BuyCreditsModal — a prominent paywall shown when an external user tries to
 * start a new audit with 0 wallet credits. Two CTAs:
 *   • Buy credits   → takes them to /pricing (where the Razorpay flow lives)
 *   • View plans    → same destination, scrolls to the bundle grid
 *
 * The modal is a hard gate: there is no "continue anyway" button. Backend
 * enforces the same rule (HTTP 402 from require_credits), but we show this
 * modal up-front so a free-tier designer never wastes time filling out a
 * form they can't submit.
 */
export default function BuyCreditsModal({ open, onClose, creditsAvailable = 0 }) {
  const navigate = useNavigate();
  if (!open) return null;

  const goToPricing = () => {
    onClose?.();
    navigate("/pricing");
  };
  const goToPlans = () => {
    onClose?.();
    navigate("/pricing#bundles");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="buy-credits-title"
      data-testid="buy-credits-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "color-mix(in srgb, var(--v-bg) 80%, transparent)",
        backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="v-surface"
        style={{
          maxWidth: 560, width: "100%",
          padding: 48,
          borderRadius: 16,
          position: "relative",
          border: "1px solid var(--v-border)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
        }}
      >
        {/* Close × */}
        <button
          onClick={onClose}
          data-testid="buy-credits-close-btn"
          aria-label="Close"
          style={{
            position: "absolute", top: 16, right: 20,
            background: "transparent", border: "none",
            color: "var(--v-text-2)", fontSize: 24, cursor: "pointer", lineHeight: 1,
          }}
        >
          ×
        </button>

        {/* Icon badge */}
        <div style={{
          width: 56, height: 56, borderRadius: 12,
          background: "color-mix(in srgb, var(--v-accent) 15%, transparent)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--v-accent)",
          marginBottom: 20,
        }}>
          <Lock size={26} />
        </div>

        <div className="overline" style={{ marginBottom: 10, color: "var(--v-accent)" }}>
          Credit required
        </div>

        <h2
          id="buy-credits-title"
          data-testid="buy-credits-title"
          style={{
            fontSize: "clamp(26px, 3.5vw, 36px)",
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
            marginBottom: 16,
          }}
        >
          {creditsAvailable === 0
            ? <>Your free credit's been used.</>
            : <>Not enough credits.</>}
        </h2>

        <p style={{ color: "var(--v-text-2)", fontSize: 16, lineHeight: 1.6, marginBottom: 32 }}>
          You need at least <b style={{ color: "var(--v-text)" }}>1 audit credit</b> to begin a new verdict.
          Top up your wallet in a minute — or upgrade to a Studio plan for better per-audit pricing.
        </p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            className="v-btn"
            onClick={goToPricing}
            data-testid="buy-credits-cta-btn"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Sparkles size={16} />
            Buy credits
            <ArrowRight size={16} />
          </button>
          <button
            className="v-btn v-btn-outline"
            onClick={goToPlans}
            data-testid="buy-credits-view-plans-btn"
          >
            View plans
          </button>
        </div>

        <p style={{ marginTop: 24, fontSize: 12, color: "var(--v-text-2)", lineHeight: 1.5 }}>
          Starter is ₹499 for 3 audits · Studio and Agency bundles bring the per-audit price down further.
          Credits never expire.
        </p>
      </div>
    </div>
  );
}
